// WHAT: Management component for TimeTracQ workspaces and aspects configuration
// WHY:  Provides comprehensive interface for creating and managing workspaces, aspects, and their settings
// DOMAIN: Settings → TimeTracQ → Workspace Management
// DEPENDENCIES: Custom hooks, UI components, workspace and aspect commands
// RISKS:
//   - WARN: Complex nested state structure may cause performance issues with large configurations
//   - NOTE: Workspace sorting order is hardcoded - may not match user expectations

import type { WorkspaceConfig } from "./types";

import {
  FolderOpen,
  CheckCircle,
  AlertCircle,
  Loader2,
  Info,
  Shield,
  Plus,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useState, useEffect } from "react";

import { Card, Button, Stack, Heading, StatusIndicator } from "@/components/ui";

import { AddAspectModal } from "./components/AddAspectModal";
import { AddWorkspaceModal } from "./components/AddWorkspaceModal";
import { WorkspaceCard } from "./components/WorkspaceCard";
import { useAspectManagement } from "./hooks/useAspectManagement";
import { useWorkspaceConfig } from "./hooks/useWorkspaceConfig";
import { useWorkspaceCreation } from "./hooks/useWorkspaceCreation";
import { useWorkspaceExpansion } from "./hooks/useWorkspaceExpansion";

/**
 * WHAT:
 *   React component for managing TimeTracQ workspaces, aspects, and their configurations.
 *
 * WHY:
 *   Provides comprehensive interface for creating workspaces, adding aspects, and configuring
 *   time allocation, status, colors, and behavior flags for optimal time tracking.
 *
 * BEHAVIOR:
 *   - Loads workspace configuration from database on mount
 *   - Supports creating new workspaces and aspects
 *   - Allows editing aspect properties (hours, status, color, flags)
 *   - Implements expandable/collapsible workspace views
 *   - Saves entire configuration to database
 *
 * RISKS:
 *   - WARN: Complex nested state updates may cause performance issues
 *   - NOTE: Workspace order is hardcoded - may not reflect user preferences
 *   - WARN: Changes are not persisted until explicit save action
 *
 * @returns {JSX.Element} TimeTracQ workspace management component
 *
 */
// Reason: Component is used internally by TimeTracQSettingsView via routing, not directly imported

export function TimeTracQWorkspaceManagement() {
  const {
    config,
    error,
    isConfigured,
    loadConfig,
    loading,
    loadWorkspaceNames,
    saveConfig,
    saving,
    setConfig,
    setError,
    setSaving,
    setSuccess,
    success,
  } = useWorkspaceConfig();

  const {
    addAspect,
    toggleAspectBoolean,
    updateAspectColor,
    updateAspectHours,
    updateAspectStatus,
  } = useAspectManagement(
    config ?? null,
    ((value: null | WorkspaceConfig) => {
      setConfig(value ?? undefined);
    }) as React.Dispatch<React.SetStateAction<null | WorkspaceConfig>>,
    ((error: null | string) => {
      setError(error ?? undefined);
    }) as (error: null | string) => void,
    ((success: null | string) => {
      setSuccess(success ?? undefined);
    }) as (success: null | string) => void,
    setSaving,
  );

  const { expandedWorkspaces, expandWorkspace, toggleAllWorkspaces, toggleWorkspaceExpansion } =
    useWorkspaceExpansion(config ?? null);

  const { addWorkspace } = useWorkspaceCreation(
    ((error: null | string) => {
      setError(error ?? undefined);
    }) as (error: null | string) => void,
    ((success: null | string) => {
      setSuccess(success ?? undefined);
    }) as (success: null | string) => void,
    setSaving,
    () => {
      void (async () => {
        await loadConfig();
        await loadWorkspaceNames();
      })();
    },
  );

  const [showAddWorkspaceModal, setShowAddWorkspaceModal] = useState(false);
  const [showAddAspectModal, setShowAddAspectModal] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [newAspectData, setNewAspectData] = useState({
    aspectName: "",
    carryover: true,
    color: "#3B82F6",
    count_hours: true,
    count_plan: true,
    hours: 4,
    status: "Include",
    workspaceName: "",
  });
  const [workspaceNames, setWorkspaceNames] = useState<string[]>([]);

  useEffect(() => {
    void loadConfig();
    void loadWorkspaceNames().then(setWorkspaceNames);
  }, [loadConfig, loadWorkspaceNames]);

  const handleAddWorkspace = async () => {
    await addWorkspace(newWorkspaceName);
    setNewWorkspaceName("");
    setShowAddWorkspaceModal(false);
  };

  const handleAddAspect = async () => {
    await addAspect(
      newAspectData.workspaceName,
      newAspectData.aspectName,
      {
        carryover: newAspectData.carryover,
        color: newAspectData.color,
        count_hours: newAspectData.count_hours,
        count_plan: newAspectData.count_plan,
        hours: newAspectData.hours,
        status: newAspectData.status,
      },
      () => {
        void (async () => {
          await loadConfig();
          expandWorkspace(newAspectData.workspaceName);
        })();
        setNewAspectData({
          aspectName: "",
          carryover: true,
          color: "#3B82F6",
          count_hours: true,
          count_plan: true,
          hours: 4,
          status: "Include",
          workspaceName: "",
        });
        setShowAddAspectModal(false);
      },
    );
  };

  const resetAspectData = () => {
    setNewAspectData({
      aspectName: "",
      carryover: true,
      color: "#3B82F6",
      count_hours: true,
      count_plan: true,
      hours: 4,
      status: "Include",
      workspaceName: "",
    });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Heading className="text-lg font-medium text-white flex items-center gap-2" level={3}>
          <FolderOpen className="w-5 h-5 text-slate-400" />
          Workspace Configuration Status
        </Heading>

        <StatusIndicator
          description="Your workspace and aspect configurations for time tracking"
          isConfigured={isConfigured}
          label="Workspace Management"
          showLock
          size="md"
        />
      </div>

      <Card className="bg-blue-500/10 border-blue-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-400 mb-1">Workspace Management</h3>
            <p className="text-sm text-blue-300">
              Manage your workspaces and their aspects for optimal time tracking. Each workspace can
              contain multiple aspects with individual configurations.
            </p>
          </div>
        </Stack>
      </Card>

      <div className="flex justify-center space-x-4">
        {config?.workspaces &&
        Object.keys(config.workspaces).some(
          (workspaceName) => Object.keys(config.workspaces[workspaceName] ?? {}).length > 4,
        ) ? (
          <Button onClick={toggleAllWorkspaces} size="md" variant="secondary">
            {expandedWorkspaces.size > 0 ? (
              <>
                <ChevronUp className="w-4 h-4 mr-2" />
                Collapse All
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-2" />
                Expand All
              </>
            )}
          </Button>
        ) : null}

        <Button
          onClick={() => {
            setShowAddWorkspaceModal(true);
          }}
          size="md"
          variant="primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Workspace
        </Button>

        <Button
          onClick={() => {
            setShowAddAspectModal(true);
          }}
          size="md"
          variant="secondary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Aspect
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.entries(config?.workspaces ?? {}).map(([workspaceName, aspects]) => (
          <WorkspaceCard
            aspects={aspects}
            isExpanded={expandedWorkspaces.has(workspaceName)}
            key={workspaceName}
            onToggleBoolean={(aspectName, field) => {
              toggleAspectBoolean(workspaceName, aspectName, field);
            }}
            onToggleExpansion={() => {
              toggleWorkspaceExpansion(workspaceName);
            }}
            onUpdateColor={(aspectName, color) => {
              updateAspectColor(workspaceName, aspectName, color);
            }}
            onUpdateHours={(aspectName, hours) => {
              updateAspectHours(workspaceName, aspectName, hours);
            }}
            onUpdateStatus={(aspectName, status) => {
              updateAspectStatus(workspaceName, aspectName, status);
            }}
            workspaceName={workspaceName}
          />
        ))}
      </div>

      <div className="flex justify-end">
        <Button
          disabled={saving}
          onClick={() => {
            void saveConfig();
          }}
          size="md"
          variant="primary"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <AddWorkspaceModal
        isOpen={showAddWorkspaceModal}
        onAdd={() => {
          void handleAddWorkspace();
        }}
        onClose={() => {
          setShowAddWorkspaceModal(false);
          setNewWorkspaceName("");
        }}
        onWorkspaceNameChange={setNewWorkspaceName}
        saving={saving}
        workspaceName={newWorkspaceName}
      />

      <AddAspectModal
        aspectData={newAspectData}
        isOpen={showAddAspectModal}
        onAdd={() => {
          void handleAddAspect();
        }}
        onAspectDataChange={(data) => {
          setNewAspectData((previous) => ({ ...previous, ...data }));
        }}
        onClose={() => {
          setShowAddAspectModal(false);
          resetAspectData();
        }}
        saving={saving}
        workspaceNames={workspaceNames}
      />

      {success ? (
        <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-green-300 text-sm font-medium">{success}</span>
          </Stack>
        </Card>
      ) : null}

      {error ? (
        <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300 text-sm font-medium">{error}</span>
          </Stack>
        </Card>
      ) : null}

      {loading ? (
        <Card className="bg-slate-700/50 border-slate-600/50" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
            <span className="text-slate-400 text-sm">Loading workspace configuration...</span>
          </Stack>
        </Card>
      ) : null}

      <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Shield className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-green-400 mb-1">Security & Data Management</h3>
            <p className="text-sm text-green-300">
              Your workspace configurations are stored securely in the Supabase database with
              encryption at rest and in transit. All settings are automatically synchronized and
              backed up, ensuring data integrity and availability.
            </p>
          </div>
        </Stack>
      </Card>
    </div>
  );
}
